import type { PetitionDetailsPageData, PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';

type SignerVelocityExperiment = PetitionDetailsPageUserData['experiments']['petitionPageSignerVelocityExperiment'];
type PetitionStatus = PetitionDetailsPageData['petition']['status'];

/**
 * The daily ignition floor: minimum signatures-in-the-last-24h before the pill renders. Below it the pill
 * is absent in every arm — there is deliberately no low-number state.
 *
 * Deliberately not read from the FCM. Pinning it to a commit keeps the value a readout was measured under
 * unambiguous; the cost is that tuning it needs a deploy, and a mid-run change splits the run.
 */
const DEFAULT_DAILY_SIGNATURE_FLOOR = 100;

/** Why an in-scope visitor still sees no pill. Reported in fixed priority order, most structural first. */
export type SignerVelocitySuppressedReason = 'not_signable' | 'below_floor';

export type SignerVelocityPlacement = 'stats_block' | 'none';

export type SignerVelocityExperimentResult = Readonly<{
	experiment: SignerVelocityExperiment;
	/** `petition_${id}`, matching the format `petition_view` uses so events join on the same key. */
	petitionId: string;
	/**
	 * False for fallback and QA-override resolutions, which are never treated and never fire `$exposure` —
	 * so anything that becomes an analysis denominator must exclude them too. Deliberately NOT folded into
	 * `qualified`: an override must still render the pill so QA can see the treatment.
	 */
	isRealAssignment: boolean;
	/**
	 * True when the pill WOULD render had this visitor been in the treatment arm. Computed identically in
	 * all three arms, so this — not the pill's presence — is what gates exposure. If it ever became
	 * arm-dependent the compared populations would diverge.
	 */
	qualified: boolean;
	/** `null` when qualified, or when the visitor is out of scope entirely (already signed / FCM disabled). */
	suppressedReason: SignerVelocitySuppressedReason | null;
	/** `'none'` for control and fake_control — this is where fake_control collapses onto control. */
	placement: SignerVelocityPlacement;
	dailySignatureCount: number;
	dailySignatureCountLocalized: string;
	floor: number;
}>;

export function useSignerVelocityExperiment(): SignerVelocityExperimentResult {
	const {
		data: {
			petition: { id, dailySignatureCount, dailySignatureCountLocalized, status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { experiments, signed },
	} = usePetitionDetailsPageUserData();

	const experiment = experiments.petitionPageSignerVelocityExperiment;
	const floor = DEFAULT_DAILY_SIGNATURE_FLOOR;

	// No device check: the experiment runs on desktop and mobile alike. Re-checks signed and the FCM even
	// though the normalizer forced control for both, so the exposure gate doesn't depend on it having run.
	const inScope = !signed && !experiment.fallback;

	// Needs a signable petition: on anything but PUBLISHED, SignatureStatsContainerInner renders nothing for
	// the pill to attach to. Hero-photo presence went with the dropped hero-overlay arm.
	const suppressedReason = getSuppressedReason({ status, dailySignatureCount, floor });
	const qualified = inScope && suppressedReason === null;

	return {
		experiment,
		petitionId: `petition_${id}`,
		isRealAssignment: !experiment.fallback && !experiment.override,
		qualified,
		suppressedReason: inScope ? suppressedReason : null,
		placement: qualified ? getPlacement(experiment.variation) : 'none',
		dailySignatureCount,
		dailySignatureCountLocalized,
		floor,
	};
}

function getSuppressedReason({
	status,
	dailySignatureCount,
	floor,
}: {
	status: PetitionStatus;
	dailySignatureCount: number;
	floor: number;
}): SignerVelocitySuppressedReason | null {
	if (status !== 'PUBLISHED') return 'not_signable';
	if (dailySignatureCount < floor) return 'below_floor';
	return null;
}

function getPlacement(variation: SignerVelocityExperiment['variation']): SignerVelocityPlacement {
	if (variation === 'variant_1') return 'stats_block';
	// control and fake_control — fake_control is bucketed separately but must render identically to control.
	return 'none';
}
